import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";
import { Interest, Topic } from "../types/category";

// Define a type for the slice state
export interface CategoryState {
  topics: {
    data: Topic[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  interests: {
    data: Interest[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  };
  options: {
    data: { [key: string]: Array<string[]> };
    waiting: boolean;
    loading: boolean;
    errors: any[];
  };
}

// Define the initial state using that type
const initialState: CategoryState = {
  topics: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    }
  },
  interests: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    }
  },
  options: {
    data: {},
    waiting: false,
    loading: false,
    errors: []
  }
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get topics
export const getCategoryTopics = createAsyncThunk(
  "category/topic/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/category/topic${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get interests
export const getCategoryInterests = createAsyncThunk(
  "category/interest/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/category/interest${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get category options
export const getCategoryOptions = createAsyncThunk(
  "category/options/get",
  async (
    args,
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const url = `/api/category/option`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get category search
export const getCategorySearch = createAsyncThunk(
  "category/search/get",
  async (
    args,
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const url = `/api/category/search`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get topics
      .addCase(getCategoryTopics.pending, (state) => {
        state.topics.loading = true;
        state.topics.errors = [];
      })
      .addCase(getCategoryTopics.fulfilled, (state, action) => {
        state.topics.loading = false;
        state.topics.data = action.payload.data.data;
        state.topics.pagination = action.payload.data.pagination;
      })
      .addCase(getCategoryTopics.rejected, (state, action) => {
        state.topics.loading = false;
        state.topics.errors = action.error.message ? [action.error.message] : action.payload as any[];
      })

      // Get interests
      .addCase(getCategoryInterests.pending, (state) => {
        state.interests.loading = true;
        state.interests.errors = [];
      })
      .addCase(getCategoryInterests.fulfilled, (state, action) => {
        state.interests.loading = false;
        state.interests.data = action.payload.data.data;
        state.interests.pagination = action.payload.data.pagination;
      })
      .addCase(getCategoryInterests.rejected, (state, action) => {
        state.interests.loading = false;
        state.interests.errors = action.error.message ? [action.error.message] : action.payload as any[];
      })

      // Get category options
      .addCase(getCategoryOptions.pending, (state) => {
        state.options.loading = true;
        state.options.errors = [];
      })
      .addCase(getCategoryOptions.fulfilled, (state, action) => {
        state.options.loading = false;
        state.options.data = action.payload.data;
      })
      .addCase(getCategoryOptions.rejected, (state, action) => {
        state.options.loading = false;
        state.options.errors = action.error.message ? [action.error.message] : action.payload as any[];
      })

      // Get category search
      .addCase(getCategorySearch.pending, (state) => {
        state.options.loading = true;
        state.options.errors = [];
      })
      .addCase(getCategorySearch.fulfilled, (state, action) => {
        state.options.loading = false;
        state.options.data = action.payload.data;
      })
      .addCase(getCategorySearch.rejected, (state, action) => {
        state.options.loading = false;
        state.options.errors = action.error.message ? [action.error.message] : action.payload as any[];
      });

  },
});

export default categorySlice.reducer;
